import { type AnchorProvider, BN, Program } from '@coral-xyz/anchor';
import {
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountIdempotentInstruction,
  createInitializeMintInstruction,
  TOKEN_2022_PROGRAM_ID,
  createInitializeMetadataPointerInstruction,
  TYPE_SIZE,
  LENGTH_SIZE,
  getMintLen,
  ExtensionType,
} from '@solana/spl-token';
import {
  type AccountInfo,
  ComputeBudgetProgram,
  type Connection,
  Keypair,
  PublicKey,
  type Signer,
  SystemProgram,
  type TransactionInstruction,
  TransactionMessage,
  TransactionSignature,
  VersionedTransaction,
} from '@solana/web3.js';
import { pack } from '@solana/spl-token-metadata';

import * as sugarMoneyIdlJson from './idl/sugar_money.json';
import type { SugarMoney } from './types/sugar_money';

import * as pSolIdlJson from './idl/p_sol.json';
import type { PSol } from './types/p_sol';

import { findPDA, divFloor } from './utils/utils';
import { PermissionError, SlippageError, TradeError } from './utils/errors';
import { SugarMoneyClusterMoniker, SugarMoneyProgramConfig } from './config';
import {
  SugarMoneyCurveDataV1AccountType,
  decodeMigrationKind,
  encodeMigrationKind,
  SugarMoneyMigrationKindEnum,
  SugarMoneyPdaType,
  SugarMoneyStateDataV1AccountType,
  SugarMoneyTradeDirectionEnum,
  SugarMoneyTradeInfoType,
  SugarMoneyTradeParamsType,
  SugarMoneyTradeTypeEnum,
  SugarMoneyCurveAccountType,
} from './types/accounts';
import { getCreatePoolKeys, getPdaPoolId } from '@raydium-io/raydium-sdk-v2';

const MetadataProgramId = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
export const PRICE_DECIMAL_PART = new BN('1000000000000000000');

export class SugarMoneyProgram {
  public program: Program<SugarMoney>;
  public wrapper: Program<PSol>;

  private addressLookupTable = {};
  private _wrapper_mint: PublicKey;
  private _fee_receiver: PublicKey;
  private _authority: PublicKey;

  private _subs: Record<string, number> = {};
  cache: Record<string, SugarMoneyCurveAccountType> = {};

  constructor(
    public provider: AnchorProvider,
    public cluster: SugarMoneyClusterMoniker = 'production',
    public config: SugarMoneyProgramConfig = new SugarMoneyProgramConfig(cluster),
    public programId: PublicKey = config.sugarMoneyProgramId,
  ) {
    const sugarMoneyIdl = { ...sugarMoneyIdlJson };
    sugarMoneyIdl.address = programId.toString();

    const pSolIdl = { ...pSolIdlJson };
    pSolIdl.address = config.wrapperProgramId.toString();

    this.program = new Program<SugarMoney>(sugarMoneyIdl, provider);
    this.wrapper = new Program<PSol>(pSolIdl, provider);
    // TODO: evil side effect, but limited backtraces are a nightmare
    Error.stackTraceLimit = 1000;
  }

  /// Convenience accessors
  public get connection(): Connection {
    return this.program.provider.connection;
  }

  public get walletPk(): PublicKey {
    return (this.program.provider as AnchorProvider).wallet.publicKey;
  }

  public setProvider(provider: AnchorProvider): void {
    const sugarMoneyIdl = { ...sugarMoneyIdlJson };
    sugarMoneyIdl.address = this.programId.toString();

    const pSolIdl = { ...pSolIdlJson };
    pSolIdl.address = this.config.wrapperProgramId.toString();
    this.program = new Program<SugarMoney>(sugarMoneyIdl, provider);
    this.wrapper = new Program<PSol>(pSolIdl, provider);
  }

  /// accounts

  private lookupPda(seed: string): SugarMoneyPdaType {
    if (!this.addressLookupTable[seed]) {
      this.addressLookupTable[seed] = findPDA([Buffer.from(seed)], this.programId);
    }

    return this.addressLookupTable[seed];
  }

  public get state(): SugarMoneyPdaType {
    return this.lookupPda('state');
  }

  public get event_authority(): SugarMoneyPdaType {
    return this.lookupPda('__event_authority');
  }

  public get fee_nft_collector(): SugarMoneyPdaType {
    return this.lookupPda('fee_nft_collector');
  }

  public get wrapper_mint(): Promise<PublicKey> {
    return (async () => {
      if (!this._wrapper_mint) {
        this._wrapper_mint = (await this.deserializeState(this.state.publicKey)).wrapperMint;
      }
      return this._wrapper_mint;
    })();
  }

  public get fee_receiver(): Promise<PublicKey> {
    return (async () => {
      if (!this._fee_receiver) {
        this._fee_receiver = (await this.deserializeState(this.state.publicKey)).feeReceiver;
      }
      return this._fee_receiver;
    })();
  }

  public get authority(): Promise<PublicKey> {
    return (async () => {
      if (!this._authority) {
        this._authority = (await this.deserializeState(this.state.publicKey)).authority;
      }
      return this._authority;
    })();
  }

  public async getBondingCurveAccounts(
    mintPubkey: PublicKey,
    token_program?: PublicKey,
  ): Promise<[SugarMoneyPdaType, SugarMoneyPdaType, PublicKey]> {
    const bondingCurvePda = findPDA(
      [Buffer.from('bonding_curve_'), mintPubkey.toBuffer()],
      this.programId,
    );

    if (!token_program) {
      try {
        const curve = await this.deserializeCurve(bondingCurvePda.publicKey);
        token_program = curve.version ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
      } catch {
        token_program = TOKEN_2022_PROGRAM_ID;
      }
    }

    const bondingCurveSolVaultPda = findPDA(
      [Buffer.from('bonding_curve_'), mintPubkey.toBuffer(), Buffer.from('_sol_vault')],
      this.programId,
    );

    const bondingCurveTokenAssociatedAccount = getAssociatedTokenAddressSync(
      mintPubkey,
      bondingCurvePda.publicKey,
      true,
      token_program,
    );

    return [bondingCurvePda, bondingCurveSolVaultPda, bondingCurveTokenAssociatedAccount];
  }

  /**
   * Fetches the global state of the Meme Police program.
   *
   * @returns The global state account.
   */
  public async deserializeState(pubkey: PublicKey): Promise<SugarMoneyStateDataV1AccountType> {
    try {
      return (await this.program.account.state.fetch(pubkey))!.data;
    } catch (e: any) {
      console.error(`Failed to deserialize state for ${pubkey.toBase58()}: ${e.message}`);
      throw new Error(`State deserialization failed: ${e.message}`);
    }
  }

  public async subscribeBondingCurve(params: {
    mint?: PublicKey;
    curve?: PublicKey;
    callback?: any;
  }): Promise<void> {
    if (!params.mint && !params.curve) {
      throw new Error('mint or bonding curve must be specified');
    }

    await this.unsubscribeBondingCurve({ mint: params.mint, curve: params.curve });

    let bondingCurve: PublicKey;
    let curveState: SugarMoneyCurveAccountType;
    if (params.mint) {
      const [bondingCurvePda] = await this.getBondingCurveAccounts(params.mint);
      bondingCurve = bondingCurvePda.publicKey;
    } else if (params.curve) {
      bondingCurve = params.curve;
    }

    const subId = this.connection.onAccountChange(
      bondingCurve,
      (accountInfo: AccountInfo<Buffer>) => {
        const deserializedData: Buffer = this.program.coder.accounts.decode(
          'curve',
          accountInfo.data,
        );
        if (params.callback) {
          params.callback(params.mint ?? params.curve);
        }
        if (params.curve)
          this.cache[params.curve.toString()] =
            deserializedData as unknown as SugarMoneyCurveAccountType;
        if (params.mint)
          this.cache[params.mint.toString()] =
            deserializedData as unknown as SugarMoneyCurveAccountType;
      },
    );

    if (params.curve) this._subs[params.curve.toString()] = subId;
    if (params.mint) this._subs[params.mint.toString()] = subId;

    try {
      curveState = await this.deserializeCurve(bondingCurve);
      if (params.curve) this.cache[params.curve.toString()] = curveState;
      if (params.mint) this.cache[params.mint.toString()] = curveState;
    } catch (e) {
      console.error(`Failed to deserialize curve for ${bondingCurve.toBase58()}: ${e.message}`);
    }
  }

  public async unsubscribeBondingCurve(params: {
    mint?: PublicKey;
    curve?: PublicKey;
  }): Promise<void> {
    if (params.curve) {
      const pidstr = params.curve.toString();
      if (this._subs[pidstr]) {
        try {
          this.connection.removeAccountChangeListener(this._subs[pidstr]);
        } finally {
          delete this._subs[pidstr];
          delete this.cache[pidstr];
        }
      }
    }

    if (params.mint) {
      const pidstr = params.mint.toString();
      if (this._subs[pidstr]) {
        try {
          this.connection.removeAccountChangeListener(this._subs[pidstr]);
        } finally {
          delete this._subs[pidstr];
          delete this.cache[pidstr];
        }
      }
    }
  }

  /**
   * Fetches the curve state for a given bonding curve.
   *
   * @param bondingCurve - The bonding curve's PDA.
   * @returns The curve account.
   */
  public async deserializeCurve(pubkey: PublicKey): Promise<SugarMoneyCurveAccountType> {
    try {
      const curve = (await this.program.account.curve.fetch(
        pubkey,
      )) as unknown as SugarMoneyCurveAccountType;
      curve.data.migrationKind = decodeMigrationKind(curve!.data.migrationKind);
      return curve;
    } catch (e: any) {
      console.error(`Failed to deserialize curve for ${pubkey.toBase58()}: ${e.message}`);
      throw new Error(`Curve deserialization failed: ${e.message}`);
    }
  }

  public async getTokenBalance(
    mint: PublicKey,
    walletAddress: PublicKey = this.walletPk,
    token_program: PublicKey = TOKEN_2022_PROGRAM_ID,
  ): Promise<BN> {
    try {
      const tokenAccount = getAssociatedTokenAddressSync(mint, walletAddress, true, token_program);
      return new BN((await this.connection.getTokenAccountBalance(tokenAccount)).value.amount);
    } catch {
      return new BN(0);
    }
  }

  /**
   * Divide by 1e18 to get the decimal price in SOL
   */
  getPrice(
    solReserve: BN,
    tokenReserve: BN,
    delta: BN,
    tradeType: SugarMoneyTradeTypeEnum,
    direction: SugarMoneyTradeDirectionEnum,
  ): BN {
    switch (tradeType) {
      case SugarMoneyTradeTypeEnum.ExactIn:
        switch (direction) {
          case SugarMoneyTradeDirectionEnum.Buy:
            return divFloor(solReserve.add(delta).mul(this.config.Precision), tokenReserve);
          case SugarMoneyTradeDirectionEnum.Sell:
            return divFloor(solReserve.mul(this.config.Precision), tokenReserve.add(delta));
        }
      case SugarMoneyTradeTypeEnum.ExactOut:
        switch (direction) {
          case SugarMoneyTradeDirectionEnum.Buy:
            return divFloor(solReserve.mul(this.config.Precision), tokenReserve.sub(delta));
          case SugarMoneyTradeDirectionEnum.Sell:
            return divFloor(solReserve.sub(delta).mul(this.config.Precision), tokenReserve);
        }
    }
  }

  normalizeTradeAmount(tradeAmount: BN, capacity: BN): BN {
    if (tradeAmount.gt(capacity)) {
      return capacity;
    } else {
      return tradeAmount;
    }
  }

  adjustForSlippage(amount: BN, slippage: number, tradeType: SugarMoneyTradeTypeEnum): BN {
    const _slippage = new BN(slippage);
    if (_slippage.gt(this.config.BpsBase)) {
      throw new SlippageError();
    }

    if (_slippage.eq(this.config.BpsBase) && tradeType == SugarMoneyTradeTypeEnum.ExactIn) {
      return new BN(0);
    }

    if (tradeType == SugarMoneyTradeTypeEnum.ExactIn) {
      return amount.sub(amount.mul(_slippage).div(this.config.BpsBase)).subn(1);
    } else {
      return amount.add(amount.mul(_slippage).div(this.config.BpsBase)).subn(1);
    }
  }

  async getTrade(
    params: SugarMoneyTradeParamsType,
  ): Promise<[SugarMoneyTradeInfoType, TradeError]> {
    let { tradeType, tradeDirection, amountToTrade, slippage, mint, receiver } = params;

    if (tradeType === SugarMoneyTradeTypeEnum.MaxOut) {
      throw new Error('unsupported trade type');
    }

    let solReserve: BN;
    let tokenReserve: BN;

    let solCapacity: BN;
    let tokenCapacity: BN;

    const globalState: SugarMoneyStateDataV1AccountType = await this.deserializeState(
      this.state.publicKey,
    );

    let bondingCurvePda: SugarMoneyPdaType;
    let curveState: SugarMoneyCurveAccountType;

    if (mint) {
      [bondingCurvePda] = await this.getBondingCurveAccounts(mint);
      try {
        curveState = await this.deserializeCurve(bondingCurvePda.publicKey);
      } catch (e) {
        console.error(
          `Failed to deserialize curve for ${bondingCurvePda.publicKey.toBase58()}: ${e.message}`,
        );
      }
    }

    if (curveState) {
      if (!curveState.data.completed.isZero()) {
        return [null, new TradeError('curve already completed')];
      }
      solReserve = curveState.data.virtualSolReserve;
      tokenReserve = curveState.data.virtualTokenReserve;

      solCapacity = await this.getCurveCapacityRemainderWithFee(
        bondingCurvePda.publicKey,
        curveState.data,
      );
      tokenCapacity = curveState.data.realTokenReserve.sub(this.config.tokensToDex);
    } else {
      solReserve = globalState.initialVirtualSolReserve;
      tokenReserve = globalState.initialVirtualTokenReserve;

      solCapacity = await this.getCurveCapacityWithFee();
      tokenCapacity = this.config.totalTokensToSell;
    }

    let info: SugarMoneyTradeInfoType = {
      inputAmount: new BN(0),
      minmaxOutputAmount: new BN(0),
      price: new BN(0),
      fee: new BN(0),
    };

    if (
      tradeType === SugarMoneyTradeTypeEnum.ExactIn &&
      tradeDirection === SugarMoneyTradeDirectionEnum.Buy &&
      amountToTrade.gte(solCapacity)
    ) {
      tradeType = SugarMoneyTradeTypeEnum.ExactOut;
      amountToTrade = tokenCapacity;
    }
    let _price: BN;
    switch (tradeType) {
      case SugarMoneyTradeTypeEnum.ExactIn:
        switch (tradeDirection) {
          case SugarMoneyTradeDirectionEnum.Buy:
            info.inputAmount = this.normalizeTradeAmount(amountToTrade, solCapacity);
            info.fee = divFloor(info.inputAmount.mul(globalState.feeBps), this.config.BpsBase);
            _price = this.getPrice(
              solReserve,
              tokenReserve,
              info.inputAmount.sub(info.fee),
              SugarMoneyTradeTypeEnum.ExactIn,
              tradeDirection,
            );
            info.price = _price.mul(PRICE_DECIMAL_PART).div(this.config.Precision);
            info.minmaxOutputAmount = this.adjustForSlippage(
              divFloor(info.inputAmount.sub(info.fee).mul(this.config.Precision), _price),
              slippage ?? 0,
              SugarMoneyTradeTypeEnum.ExactIn,
            );
            break;
          case SugarMoneyTradeDirectionEnum.Sell:
            info.inputAmount = this.normalizeTradeAmount(
              amountToTrade,
              await this.getTokenBalance(
                mint,
                this.walletPk,
                !curveState || (curveState && curveState.version)
                  ? TOKEN_2022_PROGRAM_ID
                  : TOKEN_PROGRAM_ID,
              ),
            );
            _price = this.getPrice(
              solReserve,
              tokenReserve,
              info.inputAmount,
              SugarMoneyTradeTypeEnum.ExactIn,
              tradeDirection,
            );
            info.price = _price.mul(PRICE_DECIMAL_PART).div(this.config.Precision);
            const outputWithoutFee = info.inputAmount.mul(_price).div(this.config.Precision);
            info.fee = divFloor(outputWithoutFee.mul(globalState.feeBps), this.config.BpsBase);
            info.minmaxOutputAmount = this.adjustForSlippage(
              outputWithoutFee.sub(info.fee),
              slippage ?? 0,
              SugarMoneyTradeTypeEnum.ExactIn,
            );
            break;
        }
        break;
      case SugarMoneyTradeTypeEnum.ExactOut:
        switch (tradeDirection) {
          case SugarMoneyTradeDirectionEnum.Buy:
            info.minmaxOutputAmount = this.normalizeTradeAmount(amountToTrade, tokenCapacity);
            _price = this.getPrice(
              solReserve,
              tokenReserve,
              info.minmaxOutputAmount,
              SugarMoneyTradeTypeEnum.ExactOut,
              tradeDirection,
            );
            info.price = _price.mul(PRICE_DECIMAL_PART).div(this.config.Precision);
            const solInputWithoutFee = info.minmaxOutputAmount
              .mul(_price)
              .div(this.config.Precision)
              .addn(1);
            info.fee = divFloor(
              solInputWithoutFee.mul(globalState.feeBps),
              this.config.BpsBase.sub(globalState.feeBps),
            );
            info.inputAmount = this.adjustForSlippage(
              solInputWithoutFee.add(info.fee).addn(1),
              slippage ?? 0,
              SugarMoneyTradeTypeEnum.ExactOut,
            );
            break;
          case SugarMoneyTradeDirectionEnum.Sell:
            info.minmaxOutputAmount = amountToTrade;
            info.fee = divFloor(
              info.minmaxOutputAmount.mul(globalState.feeBps),
              this.config.BpsBase.sub(globalState.feeBps),
            );
            _price = this.getPrice(
              solReserve,
              tokenReserve,
              info.minmaxOutputAmount.add(info.fee),
              SugarMoneyTradeTypeEnum.ExactOut,
              tradeDirection,
            );
            info.price = _price.mul(PRICE_DECIMAL_PART).div(this.config.Precision);
            info.inputAmount = this.adjustForSlippage(
              info.minmaxOutputAmount.add(info.fee).mul(this.config.Precision).div(_price).addn(2),
              slippage ?? 0,
              SugarMoneyTradeTypeEnum.ExactOut,
            );
            break;
        }
        break;
    }

    if (mint) {
      info.ix =
        tradeDirection == SugarMoneyTradeDirectionEnum.Buy
          ? await this.buyIx(
              mint,
              info.inputAmount,
              info.minmaxOutputAmount,
              receiver ?? this.walletPk,
              tradeType,
              !curveState || (curveState && curveState.version)
                ? TOKEN_2022_PROGRAM_ID
                : TOKEN_PROGRAM_ID,
            )
          : await this.sellIx(
              mint,
              info.inputAmount,
              info.minmaxOutputAmount,
              receiver ?? this.walletPk,
              tradeType,
              !curveState || (curveState && curveState.version)
                ? TOKEN_2022_PROGRAM_ID
                : TOKEN_PROGRAM_ID,
            );
    }

    return [info, null];
  }

  /**
   * Computes the total SOL capacity for selling the entire supply of tokens.
   * This shows how much SOL would be required to fully buy out the bonding curve.
   *
   * @returns The total SOL capacity as a BN.
   */
  async getCurveCapacity(): Promise<BN> {
    const globalState: SugarMoneyStateDataV1AccountType = await this.deserializeState(
      this.state.publicKey,
    );

    const price = divFloor(
      globalState.initialVirtualSolReserve.mul(this.config.Precision),
      globalState.initialVirtualTokenReserve.sub(this.config.totalTokensToSell),
    );

    return this.config.totalTokensToSell.mul(price).div(this.config.Precision).addn(1);
  }

  /**
   * Computes the total SOL capacity for selling the entire supply of tokens.
   * This shows how much SOL would be required to fully buy out the bonding curve.
   *
   * @returns The total SOL capacity as a BN.
   */
  async getCurveCapacityWithFee(): Promise<BN> {
    const globalState: SugarMoneyStateDataV1AccountType = await this.deserializeState(
      this.state.publicKey,
    );

    const price = divFloor(
      globalState.initialVirtualSolReserve.mul(this.config.Precision),
      globalState.initialVirtualTokenReserve.sub(this.config.totalTokensToSell),
    );

    let capacity = this.config.totalTokensToSell.mul(price).div(this.config.Precision);
    const fee = capacity.mul(globalState.feeBps).div(this.config.BpsBase.sub(globalState.feeBps));
    capacity = capacity.add(fee);

    return capacity;
  }

  /**
   * Computes the total SOL capacity for selling the current supply of tokens.
   * This shows how much SOL would be required to fully buy out the bonding curve.
   *
   * @param bondingCurve - The bonding curve's PDA.
   * @returns The current SOL capacity as a BN.
   */
  async getCurveCapacityRemainder(
    bondingCurve: PublicKey,
    recentState?: SugarMoneyCurveDataV1AccountType,
  ): Promise<BN> {
    const curveState: SugarMoneyCurveDataV1AccountType = recentState
      ? recentState
      : (await this.deserializeCurve(bondingCurve)).data;

    const tokensRemainder = curveState.realTokenReserve.sub(this.config.tokensToDex);

    const price = divFloor(
      curveState.virtualSolReserve.mul(this.config.Precision),
      curveState.virtualTokenReserve.sub(tokensRemainder),
    );

    return tokensRemainder.mul(price).div(this.config.Precision).addn(1);
  }

  async getCurveCapacityRemainderWithFee(
    bondingCurve: PublicKey,
    recentState?: SugarMoneyCurveDataV1AccountType,
  ): Promise<BN> {
    const curveState: SugarMoneyCurveDataV1AccountType = recentState
      ? recentState
      : (await this.deserializeCurve(bondingCurve)).data;
    const globalState: SugarMoneyStateDataV1AccountType = await this.deserializeState(
      this.state.publicKey,
    );

    const tokensRemainder = curveState.realTokenReserve.sub(this.config.tokensToDex);

    const price = divFloor(
      curveState.virtualSolReserve.mul(this.config.Precision),
      curveState.virtualTokenReserve.sub(tokensRemainder),
    );

    let capacity = tokensRemainder.mul(price).div(this.config.Precision);
    const fee = capacity.mul(globalState.feeBps).div(this.config.BpsBase.sub(globalState.feeBps));
    capacity = capacity.add(fee);

    return capacity;
  }

  /**
   * Computes the completion percentage of the bonding curve,
   * i.e., what percentage of the total token supply has been effectively 'sold'.
   *
   * @param bondingCurve - The bonding curve's PDA.
   * @returns The completion percentage (0-100).
   */
  async getCompletionPercent(bondingCurve: PublicKey): Promise<number> {
    const curveState: SugarMoneyCurveDataV1AccountType = (await this.deserializeCurve(bondingCurve))
      .data;
    if (!curveState.completed.isZero()) {
      return 1;
    }

    const numerator = this.config.totalSupply
      .sub(curveState.realTokenReserve)
      .mul(this.config.Precision)
      .mul(new BN(100));
    const fraction = numerator.div(this.config.totalTokensToSell);

    return Number(fraction) / Number(this.config.Precision);
  }

  /// Transactions
  public async createProgramAccountIx(
    authority: PublicKey,
    size: number,
    programId: PublicKey,
  ): Promise<[TransactionInstruction, Keypair]> {
    const lamports = await this.connection.getMinimumBalanceForRentExemption(size);
    const address = Keypair.generate();

    const ix = SystemProgram.createAccount({
      fromPubkey: authority,
      newAccountPubkey: address.publicKey,
      lamports,
      space: size,
      programId,
    });
    return [ix, address];
  }

  async createAssociatedTokenAccountIfNotExistIx(
    owner: PublicKey,
    mint: PublicKey,
    allowOwnerOffCurve = false,
    payer?: PublicKey,
    token_program?: PublicKey,
  ): Promise<[PublicKey, TransactionInstruction]> {
    const associatedAccount = getAssociatedTokenAddressSync(
      mint,
      owner,
      allowOwnerOffCurve,
      token_program ?? TOKEN_2022_PROGRAM_ID,
    );
    const associatedAccountInfo = await this.provider.connection.getAccountInfo(associatedAccount);
    let ix: TransactionInstruction;
    if (!associatedAccountInfo) {
      ix = createAssociatedTokenAccountIdempotentInstruction(
        payer ?? owner,
        associatedAccount,
        owner,
        mint,
        token_program ?? TOKEN_2022_PROGRAM_ID,
      );
    }
    return [associatedAccount, ix];
  }

  /**
   * Creates an `initialize` instruction setting up initial token and SOL reserves and fee parameters.
   *
   * @param initialVirtualTokenReserve - Initial virtual token reserve.
   * @param initialVirtualSolReserve - Initial virtual SOL reserve.
   * @param wrapperMint - Wrapper mint address.
   * @param feeReceiver - Fee receiver address.
   * @param feeBps - Fee in basis points.
   * @returns The `initialize` instruction.
   */
  public async initializeIx(
    initialVirtualTokenReserve: number | BN,
    initialVirtualSolReserve: number | BN,
    migrator: PublicKey,
    wrapperMint: PublicKey,
    feeReceiver: PublicKey,
    feeBps: number | BN,
    coinLotSize: number | BN,
    pcLotSize: number | BN,
  ): Promise<TransactionInstruction> {
    const ix = this.program.methods
      .initialize(
        migrator,
        wrapperMint,
        feeReceiver,
        new BN(feeBps),
        new BN(initialVirtualTokenReserve),
        new BN(initialVirtualSolReserve),
        new BN(coinLotSize),
        new BN(pcLotSize),
      )
      .accounts({
        // @ts-ignore
        state: this.state.publicKey,
        signer: this.walletPk,
      })
      .instruction();

    return ix;
  }

  private async setParam(method: string, ...args: any[]) {
    if (!this.walletPk.equals(await this.authority)) {
      throw new PermissionError();
    }

    const ix = this.program.methods[method](...args)
      .accounts({
        state: this.state.publicKey,
        authority: this.walletPk,
      })
      .instruction();

    return ix;
  }

  public setWrapperMintIx(wrapperMint: PublicKey): Promise<TransactionInstruction> {
    return this.setParam('setWrapperMint', wrapperMint);
  }

  public setFeeReceiverIx(feeReceiver: PublicKey): Promise<TransactionInstruction> {
    return this.setParam('setFeeReceiver', feeReceiver);
  }

  public setFeeBpsIx(feeBps: number | BN): Promise<TransactionInstruction> {
    return this.setParam('setFeeBps', new BN(feeBps));
  }

  public setCurveParamsIx(
    initialTokenVirtualReserve: number | BN,
    initialSolVirtualReserve: number | BN,
  ): Promise<TransactionInstruction> {
    return this.setParam('setCurveParams', initialTokenVirtualReserve, initialSolVirtualReserve);
  }

  public setMigrationLotSizeIx(
    coinLotSize: number | BN,
    pcLotSize: number | BN,
  ): Promise<TransactionInstruction> {
    return this.setParam('setMigrationLotSize', coinLotSize, pcLotSize);
  }

  public setMigratorIx(migrator: PublicKey): Promise<TransactionInstruction> {
    return this.setParam('setMigrator', migrator);
  }

  public setMigrationRefundIx(amount: BN): Promise<TransactionInstruction> {
    return this.setParam('setMigrationRefund', amount);
  }

  /**
   * Creates a `set_pending_authority` instruction to propose a new authority for the program.
   *
   * @param pendingAuthority - The proposed new authority.
   * @returns The `set_pending_authority` instruction.
   */
  async setPendingAuthorityIx(pendingAuthority: PublicKey): Promise<TransactionInstruction> {
    const ix = this.program.methods
      .setPendingAuthority(pendingAuthority)
      .accounts({
        state: this.state.publicKey,
        authority: this.walletPk,
      })
      .instruction();

    return ix;
  }
  /**
   * Creates a `create` instruction to mint a new token and set up the bonding curve.
   * Also returns metadata, bonding curve, and associated token PDAs.
   *
   * @param name - Token name.
   * @param symbol - Token symbol.
   * @param uri - Token metadata URI.
   * @param mint - Keypair for the mint.
   * @returns A tuple: [TransactionInstruction[], Signer].
   */
  async createIx(
    name: string,
    symbol: string,
    uri: string,
    mint?: PublicKey,
    migrationKind = SugarMoneyMigrationKindEnum.pSol,
  ): Promise<[TransactionInstruction[], Signer]> {
    let mintKeypair: Keypair;
    if (!mint) {
      mintKeypair = Keypair.generate();
      mint = mintKeypair.publicKey;
    }

    const [bondingCurvePda, bondingCurveSolVaultPda, bondingCurveTokenAssociatedAccount] =
      await this.getBondingCurveAccounts(mint, TOKEN_2022_PROGRAM_ID);

    const metaData = {
      updateAuthority: bondingCurvePda.publicKey,
      mint: mint,
      name,
      symbol,
      uri,
      additionalMetadata: [],
    };

    const metadataExtension = TYPE_SIZE + LENGTH_SIZE;
    // Size of metadata
    const metadataLen = pack(metaData).length;

    // Size of Mint Account with extension
    const mintLen = getMintLen([ExtensionType.MetadataPointer]);

    const preIxs = [
      SystemProgram.createAccount({
        fromPubkey: this.walletPk,
        newAccountPubkey: mint,
        space: mintLen,
        lamports: await this.connection.getMinimumBalanceForRentExemption(
          mintLen + metadataExtension + metadataLen,
          'processed',
        ),
        programId: TOKEN_2022_PROGRAM_ID,
      }),
      createInitializeMetadataPointerInstruction(
        mint,
        bondingCurvePda.publicKey,
        mint,
        TOKEN_2022_PROGRAM_ID,
      ),
      createInitializeMintInstruction(
        mint,
        6,
        bondingCurvePda.publicKey,
        null,
        TOKEN_2022_PROGRAM_ID,
      ),
      createAssociatedTokenAccountIdempotentInstruction(
        this.walletPk,
        bondingCurveTokenAssociatedAccount,
        bondingCurvePda.publicKey,
        mint,
        TOKEN_2022_PROGRAM_ID,
      ),
    ];

    const ix = await this.program.methods
      // @ts-ignore
      .create(name, symbol, uri, encodeMigrationKind(migrationKind))
      .accounts({
        // @ts-ignore
        state: this.state.publicKey,
        mint: mint,
        bondingCurve: bondingCurvePda.publicKey,
        bondingCurveSolVault: bondingCurveSolVaultPda.publicKey,
        bondingCurveTokenAssociatedAccount: bondingCurveTokenAssociatedAccount,
        tokenMetadataProgram: MetadataProgramId,
        payer: this.walletPk,
        eventAuthority: this.event_authority.publicKey,
      })
      .instruction();

    return [[...preIxs, ix], mintKeypair ?? undefined];
  }

  /**
   * Creates a `buy` instruction to purchase `exactTokensOutput` tokens for up to `maxSolAmount` SOL.
   *
   * @param mintPublicKey - The token mint public key.
   * @param solAmountInput - The max SOL to spend.
   * @param minTokensOutput - The minimum number of tokens to buy.
   * @param receiver - Optional token receiver (defaults to payer).
   * @returns The `buy` instruction.
   */
  async buyIx(
    mint: PublicKey,
    solAmountInput: number | BN,
    minTokensOutput: number | BN,
    receiver: PublicKey = this.walletPk,
    tradeType: SugarMoneyTradeTypeEnum = SugarMoneyTradeTypeEnum.ExactIn,
    token_program: PublicKey = TOKEN_2022_PROGRAM_ID,
  ): Promise<TransactionInstruction> {
    const receiverAssociatedAccount = getAssociatedTokenAddressSync(
      mint,
      receiver,
      true,
      token_program,
    );
    const [bondingCurvePda, bondingCurveSolVaultPda, bondingCurveTokenAssociatedAccount] =
      await this.getBondingCurveAccounts(mint, token_program);

    let handler;
    switch (tradeType) {
      case SugarMoneyTradeTypeEnum.ExactIn:
        handler = this.program.methods.buyExactIn;
        break;
      case SugarMoneyTradeTypeEnum.ExactOut:
        handler = this.program.methods.buyExactOut;
        break;
      case SugarMoneyTradeTypeEnum.MaxOut:
        handler = this.program.methods.buyMaxOut;
        break;
    }

    const ix = handler(
      {
        bondingCurve: bondingCurvePda.nonce,
        bondingCurveSolAssociatedAccount: bondingCurveSolVaultPda.nonce,
      },
      new BN(solAmountInput),
      new BN(minTokensOutput),
    )
      .accounts({
        // @ts-ignore
        state: this.state.publicKey,
        mint,
        bondingCurve: bondingCurvePda.publicKey,
        bondingCurveSolAssociatedAccount: bondingCurveSolVaultPda.publicKey,
        bondingCurveTokenAssociatedAccount,
        receiverAssociatedAccount,
        payer: this.walletPk,
        receiver: receiver,
        feeReceiver: await this.fee_receiver,
        tokenProgram: token_program,
        eventAuthority: this.event_authority.publicKey,
      })
      .instruction();

    return ix;
  }

  /**
   * Creates a `sell` instruction to sell `exactTokensInput` tokens and receive at least `minSolAmount` in SOL.
   *
   * @param mintPublicKey - The token mint public key.
   * @param exactTokensInput - Exact tokens to sell.
   * @param minSolAmount - Minimum SOL to receive.
   * @param receiver - Optional SOL receiver (defaults to payer).
   * @returns The `sell` instruction.
   */
  async sellIx(
    mint: PublicKey,
    tokensAmountInput: number | BN,
    minSolAmountOutput: number | BN,
    receiver: PublicKey = this.walletPk,
    tradeType: SugarMoneyTradeTypeEnum = SugarMoneyTradeTypeEnum.ExactIn,
    token_program: PublicKey = TOKEN_2022_PROGRAM_ID,
  ): Promise<TransactionInstruction> {
    const payerAssociatedAddress = getAssociatedTokenAddressSync(
      mint,
      this.walletPk,
      false,
      token_program,
    );
    const [bondingCurvePda, bondingCurveSolVaultPda, bondingCurveTokenAssociatedAccount] =
      await this.getBondingCurveAccounts(mint, token_program);

    let handler;
    switch (tradeType) {
      case SugarMoneyTradeTypeEnum.ExactIn:
        handler = this.program.methods.sellExactIn;
        break;
      case SugarMoneyTradeTypeEnum.ExactOut:
        handler = this.program.methods.sellExactOut;
        break;
      case SugarMoneyTradeTypeEnum.MaxOut:
        throw new Error('unsupported sell trade type');
    }

    const ix = handler(
      {
        bondingCurve: bondingCurvePda.nonce,
        bondingCurveSolAssociatedAccount: bondingCurveSolVaultPda.nonce,
      },
      new BN(tokensAmountInput),
      new BN(minSolAmountOutput),
    )
      .accounts({
        // @ts-ignore
        state: this.state.publicKey,
        mint,
        bondingCurve: bondingCurvePda.publicKey,
        bondingCurveSolAssociatedAccount: bondingCurveSolVaultPda.publicKey,
        bondingCurveTokenAssociatedAccount,
        payerAssociatedAccount: payerAssociatedAddress,
        payer: this.walletPk,
        receiver: receiver,
        feeReceiver: await this.fee_receiver,
        tokenProgram: token_program,
        eventAuthority: this.event_authority.publicKey,
      })
      .instruction();

    return ix;
  }

  // Migration

  async wrapSolForBondingCurveIx(tokenMint: PublicKey): Promise<TransactionInstruction[]> {
    const [bondingCurvePda, bondingCurveSolVaultPda] =
      await this.getBondingCurveAccounts(tokenMint);

    let wrapperProgram: PublicKey;
    let wrapperMintAuthority: PublicKey;
    let wrapperState: PublicKey;
    let wrapperMint: PublicKey;

    const curveState: SugarMoneyCurveDataV1AccountType = (
      await this.deserializeCurve(bondingCurvePda.publicKey)
    ).data;

    switch (curveState.migrationKind) {
      case SugarMoneyMigrationKindEnum.pSol:
        wrapperProgram = this.wrapper.programId;
        wrapperMintAuthority = findPDA(
          [Buffer.from('mint_authority')],
          this.wrapper.programId,
        ).publicKey;
        wrapperState = findPDA([Buffer.from('state')], this.wrapper.programId).publicKey;
        wrapperMint = await this.wrapper_mint;
        break;
      case SugarMoneyMigrationKindEnum.wSol:
        wrapperMint = this.config.wsolMintAddress;
        // due to nature of account serialization next assignments aren't increase costs
        wrapperProgram = wrapperMint;
        wrapperMintAuthority = wrapperMint;
        wrapperState = wrapperMint;
        break;
      default:
        throw new Error('unknown migration kind');
    }

    const [wrapperBondingCurveTokenAccount, wrapperBondingCurveTokenAccountIx] =
      await this.createAssociatedTokenAccountIfNotExistIx(
        bondingCurvePda.publicKey,
        wrapperMint,
        true,
        this.walletPk,
        TOKEN_PROGRAM_ID,
      );

    const ix = await this.program.methods
      .wrapSolForCurve()
      .accounts({
        // @ts-ignore
        state: this.state,
        tokenMint,
        bondingCurve: bondingCurvePda.publicKey,
        bondingCurveSolVault: bondingCurveSolVaultPda.publicKey,
        wrapperProgram: wrapperProgram,
        wrapperState: wrapperState,
        wrapperMint: wrapperMint,
        wrapperMintAuthority: wrapperMintAuthority,
        wrapperBondingCurveTokenAccount: wrapperBondingCurveTokenAccount,
        payer: this.walletPk,
      })
      .instruction();

    let ixs = [wrapperBondingCurveTokenAccountIx ?? null, ix];

    return ixs.filter((ix) => ix != null);
  }

  async migrateToRadiumIx(
    tokenMint: PublicKey,
    feeNftMint: PublicKey,
  ): Promise<TransactionInstruction[]> {
    const [bondingCurvePda, _bondingCurveSolVaultPda, bondingCurveTokenAssociatedAccount] =
      await this.getBondingCurveAccounts(tokenMint);

    const curve: SugarMoneyCurveAccountType = await this.deserializeCurve(
      bondingCurvePda.publicKey,
    );

    const curveState: SugarMoneyCurveDataV1AccountType = curve.data;

    let wrapperMint: PublicKey;
    switch (curveState.migrationKind) {
      case SugarMoneyMigrationKindEnum.pSol:
        wrapperMint = await this.wrapper_mint;
        break;
      case SugarMoneyMigrationKindEnum.wSol:
        wrapperMint = this.config.wsolMintAddress;
        break;
      default:
        throw new Error('unknown migration kind');
    }

    const wTt = new BN(wrapperMint.toBuffer()).lt(new BN(tokenMint.toBuffer()));

    const [mintA, mintB] = wTt ? [wrapperMint, tokenMint] : [tokenMint, wrapperMint];

    const { publicKey: ammPoolId } = getPdaPoolId(
      this.config.raydiumAmmProgramId,
      this.config.ammConfigId,
      mintA,
      mintB,
    );

    const poolKeys = getCreatePoolKeys({
      poolId: ammPoolId,
      programId: this.config.raydiumAmmProgramId,
      configId: this.config.ammConfigId,
      mintA,
      mintB,
    });

    const bondingCurveWrapperAssociatedAccount = getAssociatedTokenAddressSync(
      wrapperMint,
      bondingCurvePda.publicKey,
      true,
      TOKEN_PROGRAM_ID,
    );

    const [payerMintAAssociatedAccount, createMintAAccountIx] =
      await this.createAssociatedTokenAccountIfNotExistIx(
        this.walletPk,
        mintA,
        undefined,
        undefined,
        wTt ? TOKEN_PROGRAM_ID : curve.version ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID,
      );
    const [payerMintBAssociatedAccount, createMintBAccountIx] =
      await this.createAssociatedTokenAccountIfNotExistIx(
        this.walletPk,
        mintB,
        undefined,
        undefined,
        !wTt ? TOKEN_PROGRAM_ID : curve.version ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID,
      );

    const userTokenLp = getAssociatedTokenAddressSync(poolKeys.lpMint, this.walletPk);

    const wrapperState = findPDA([Buffer.from('state')], this.wrapper.programId);
    const wrapperMintAuthority = findPDA([Buffer.from('mint_authority')], this.wrapper.programId);

    const lockingAuthority = findPDA(
      [Buffer.from('lock_cp_authority_seed')],
      this.config.raydiumLockingProgramId,
    );
    const feeNftAccount = getAssociatedTokenAddressSync(
      feeNftMint,
      this.fee_nft_collector.publicKey,
      true,
    );
    const lockedLiquidity = findPDA(
      [Buffer.from('locked_liquidity'), feeNftMint.toBuffer()],
      this.config.raydiumLockingProgramId,
    );
    const lockedLpVault = getAssociatedTokenAddressSync(
      poolKeys.lpMint,
      lockingAuthority.publicKey,
      true,
    );

    const metadataPda = findPDA(
      [Buffer.from('metadata'), MetadataProgramId.toBuffer(), feeNftMint.toBuffer()],
      MetadataProgramId,
    );

    const ix = await this.program.methods
      .migrateToRadium()
      .accounts({
        tokenMint: tokenMint,
        wrapperMint: wrapperMint,
        // @ts-ignore
        bondingCurve: bondingCurvePda.publicKey,
        bondingCurveWrapperAssociatedAccount: bondingCurveWrapperAssociatedAccount,
        bondingCurveTokenAssociatedAccount: bondingCurveTokenAssociatedAccount,
        payerWrapperAssociatedAccount: wTt
          ? payerMintAAssociatedAccount
          : payerMintBAssociatedAccount,
        payerTokenAssociatedAccount: wTt
          ? payerMintBAssociatedAccount
          : payerMintAAssociatedAccount,
        state: this.state.publicKey,
        cpSwapProgram: this.config.raydiumAmmProgramId,
        ammConfig: this.config.ammConfigId,
        authority: poolKeys.authority,
        poolState: ammPoolId,
        token0Mint: mintA,
        token1Mint: mintB,
        lpMint: poolKeys.lpMint,
        creatorToken0: payerMintAAssociatedAccount,
        creatorToken1: payerMintBAssociatedAccount,
        creatorLpToken: userTokenLp,
        token0Vault: poolKeys.vaultA,
        token1Vault: poolKeys.vaultB,
        createPoolFee: this.config.raydiumCreateFeeAddress,
        observationState: poolKeys.observationId,
        lockingProgram: this.config.raydiumLockingProgramId,
        lockingAuthority,
        feeNftOwner: this.fee_nft_collector.publicKey,
        feeNftMint,
        feeNftAccount,
        lockedLiquidity,
        lockedLpVault,
        metadataAccount: metadataPda.publicKey,
        payer: this.walletPk,
        token0Program: wTt
          ? TOKEN_PROGRAM_ID
          : curve.version
            ? TOKEN_2022_PROGRAM_ID
            : TOKEN_PROGRAM_ID,
        token1Program: !wTt
          ? TOKEN_PROGRAM_ID
          : curve.version
            ? TOKEN_2022_PROGRAM_ID
            : TOKEN_PROGRAM_ID,
        wrapperProgram: this.wrapper.programId,
        wrapperState: wrapperState.publicKey,
        wrapperMintAuthority: wrapperMintAuthority.publicKey,
        metadataProgram: MetadataProgramId,
      })
      .instruction();
    let ixs = [createMintAAccountIx ?? null, createMintBAccountIx ?? null, ix];

    ixs.unshift(ComputeBudgetProgram.setComputeUnitLimit({ units: 694_200 }));

    return ixs.filter((ix) => ix != null);
  }

  async migrateToRadiumTx(
    tokenMint: PublicKey,
    signer: Keypair,
    lookupTableAccountAddress: PublicKey = this.config.accountLookupTable,
  ): Promise<[execute: () => Promise<TransactionSignature>, transaction: VersionedTransaction]> {
    const lookupTableAccount = (await this.provider.connection.getAddressLookupTable(
      lookupTableAccountAddress,
    ))!.value;

    const nftMint = Keypair.generate();
    const messageV0 = new TransactionMessage({
      payerKey: signer.publicKey,
      recentBlockhash: (await this.provider.connection.getLatestBlockhash()).blockhash,
      instructions: await this.migrateToRadiumIx(tokenMint, nftMint.publicKey),
    }).compileToV0Message([lookupTableAccount]);
    const transactionV0 = new VersionedTransaction(messageV0);
    transactionV0.sign([signer, nftMint]);

    console.log(transactionV0);

    const execute = async () => {
      return this.provider.sendAndConfirm(transactionV0);
    };

    return [execute, transactionV0];
  }

  async syncWrapper(): Promise<TransactionInstruction> {
    const wrapperMintAuthority = findPDA([Buffer.from('mint_authority')], this.wrapper.programId);

    const wrapperState = findPDA([Buffer.from('state')], this.wrapper.programId);
    const wrapperMint = await this.wrapper_mint;
    const feeReceiver = await this.fee_receiver;

    const feeReceiverTokenAccount = getAssociatedTokenAddressSync(wrapperMint, feeReceiver, true);

    const ix = this.program.methods
      .syncWrapper()
      .accounts({
        // @ts-ignore
        state: this.state,
        wrapperProgram: this.wrapper.programId,
        wrapperState: wrapperState.publicKey,
        wrapperMint: wrapperMint,
        wrapperMintAuthority: wrapperMintAuthority.publicKey,
        feeReceiverTokenAccount: feeReceiverTokenAccount,
        payer: this.walletPk,
        feeReceiver: feeReceiver,
      })
      .instruction();

    return ix;
  }

  async claimCpFeesIxs(
    poolId: PublicKey,
    feeNftMint: PublicKey,
    lpMint: PublicKey,
    mintA: PublicKey,
    mintB: PublicKey,
  ): Promise<TransactionInstruction[]> {
    const feeNftCollector = findPDA([Buffer.from('fee_nft_collector')], this.programId);

    const lockingAuthority = findPDA(
      [Buffer.from('lock_cp_authority_seed')],
      this.config.raydiumLockingProgramId,
    );
    const feeNftAccount = getAssociatedTokenAddressSync(
      feeNftMint,
      this.fee_nft_collector.publicKey,
      true,
    );

    const lockedLiquidity = findPDA(
      [Buffer.from('locked_liquidity'), feeNftMint.toBuffer()],
      this.config.raydiumLockingProgramId,
    );

    const lockedLpVault = getAssociatedTokenAddressSync(lpMint, lockingAuthority.publicKey, true);

    const cpAuthority = findPDA(
      [Buffer.from('vault_and_lp_mint_auth_seed')],
      this.config.raydiumAmmProgramId,
    );

    const referralPoolSolAccount = findPDA(
      [Buffer.from('referral-pool')],
      this.config.distributorProgramId,
    );

    const wrapperState = findPDA([Buffer.from('state')], this.config.wrapperProgramId);

    const mintInfos = await this.connection.getMultipleAccountsInfo([mintA, mintB]);
    const mintATokenProgram = mintInfos[0].owner;
    const mintBTokenProgram = mintInfos[1].owner;

    const recipientToken0Account = getAssociatedTokenAddressSync(
      mintA,
      feeNftCollector.publicKey,
      true,
      mintATokenProgram,
    );

    const recipientToken1Account = getAssociatedTokenAddressSync(
      mintB,
      feeNftCollector.publicKey,
      true,
      mintBTokenProgram,
    );

    const poolKeys = getCreatePoolKeys({
      poolId: poolId,
      programId: this.config.raydiumAmmProgramId,
      configId: this.config.ammConfigId,
      mintA,
      mintB,
    });

    const ix = await this.program.methods
      .collectCpFees(feeNftCollector.nonce)
      .accounts({
        // @ts-ignore
        state: this.state,
        lockingProgram: this.config.raydiumLockingProgramId,
        authority: lockingAuthority,
        feeNftOwner: feeNftCollector,
        feeNftAccount: feeNftAccount,
        lockedLiquidity: lockedLiquidity.publicKey,
        cpmmProgram: this.config.raydiumAmmProgramId,
        cpAuthority: cpAuthority,
        poolState: poolId,
        lpMint: lpMint,
        referralPoolSolAccount: referralPoolSolAccount.publicKey,
        wrapperProgram: this.config.wrapperProgramId,
        wrapperState: wrapperState.publicKey,
        wrapperMint: this.config.wrapperMintKey,
        recipientToken0Account,
        recipientToken1Account,
        token0Vault: poolKeys.vaultA,
        token1Vault: poolKeys.vaultB,
        vault0Mint: mintA,
        vault1Mint: mintB,
        lockedLpVault: lockedLpVault,
        token0Program: mintATokenProgram,
        token1Program: mintBTokenProgram,
      })
      .instruction();

    return [ComputeBudgetProgram.setComputeUnitLimit({ units: 300_000 }), ix];
  }

  // wrapper transactions

  async initilizePsolIx(
    name: String,
    symbol: String,
    uri: String,
    mint: Signer = Keypair.generate(),
  ): Promise<TransactionInstruction> {
    const state = findPDA([Buffer.from('state')], this.wrapper.programId);

    const mintAuthority = findPDA([Buffer.from('mint_authority')], this.wrapper.programId);

    const [metadataAddress] = PublicKey.findProgramAddressSync(
      [Buffer.from('metadata'), MetadataProgramId.toBuffer(), mint.publicKey.toBuffer()],
      MetadataProgramId,
    );

    const ix = this.wrapper.methods
      // @ts-ignore
      .initialize(name, symbol, uri)
      .accounts({
        // @ts-ignore
        state: state.publicKey,
        mint: mint.publicKey,
        mintAuthority: mintAuthority.publicKey,
        metadata: metadataAddress,
        tokenMetadataProgram: MetadataProgramId,
        payer: this.walletPk,
      })
      .instruction();

    return ix;
  }

  async wrapSolIx(
    amount: number | BN,
    receiver: PublicKey = this.walletPk,
  ): Promise<TransactionInstruction> {
    const state = findPDA([Buffer.from('state')], this.wrapper.programId);

    const mintAuthority = findPDA([Buffer.from('mint_authority')], this.wrapper.programId);

    const wrapperMint = this.config.wrapperMintKey;
    const userTokenAccount = getAssociatedTokenAddressSync(wrapperMint, receiver, true);

    const ix = this.wrapper.methods
      // @ts-ignore
      .wrapSol(new BN(amount))
      .accounts({
        // @ts-ignore

        state: state.publicKey,
        mint: wrapperMint,
        mintAuthority: mintAuthority.publicKey,
        userTokenAccount: userTokenAccount,
        payer: this.walletPk,
        receiver,
      })
      .instruction();

    return ix;
  }

  async unwrapSolIx(amount: number | BN): Promise<TransactionInstruction> {
    const state = findPDA([Buffer.from('state')], this.wrapper.programId);

    const wrapperMint = this.config.wrapperMintKey;
    const userTokenAccount = getAssociatedTokenAddressSync(wrapperMint, this.walletPk);

    const ix = this.wrapper.methods
      .unwrapSol(new BN(amount))
      .accounts({
        // @ts-ignore
        state: state.publicKey,
        mint: wrapperMint,
        userTokenAccount: userTokenAccount,
        payer: this.walletPk,
      })
      .instruction();

    return ix;
  }

  async addWrapperMinterIx(minter: PublicKey): Promise<TransactionInstruction> {
    const state = findPDA([Buffer.from('state')], this.wrapper.programId);

    const ix = this.wrapper.methods
      .addMinter(minter)
      .accounts({
        // @ts-ignore
        state: state.publicKey,
        authority: this.walletPk,
      })
      .instruction();

    return ix;
  }

  async removeWrapperMinterIx(minter: PublicKey): Promise<TransactionInstruction> {
    const state = findPDA([Buffer.from('state')], this.wrapper.programId);

    const ix = this.wrapper.methods
      .removeMinter(minter)
      .accounts({
        // @ts-ignore
        state: state.publicKey,
        authority: this.walletPk,
      })
      .instruction();

    return ix;
  }
}

export async function getFilteredProgramAccounts(
  connection: Connection,
  programId: PublicKey,
  filters,
): Promise<Record<string, AccountInfo<Buffer>>> {
  // @ts-expect-error not need check
  const resp = await connection._rpcRequest('getProgramAccounts', [
    programId.toBase58(),
    {
      commitment: connection.commitment,
      filters,
      encoding: 'base64',
    },
  ]);
  if (resp.error) {
    throw new Error(resp.error.message);
  }

  const res = {};

  resp.result.map(
    ({ pubkey, account: { data, executable, owner, lamports } }) =>
      (res[new PublicKey(pubkey).toString()] = {
        data: Buffer.from(data[0], 'base64'),
        executable,
        owner: new PublicKey(owner),
        lamports,
      }),
  );

  return res;
}
